import React from 'react';
import { RegulatoryRequirement, RegulatoryFramework } from '../types';
import { useTableSort, SortableTh } from '../../../../shared/useTableSort';

interface RequirementsCatalogTableProps {
  requirements: RegulatoryRequirement[];
  frameworks: RegulatoryFramework[];
  selectedFrameworkId: string;
  searchQuery: string;
  selectedType: string;
  loading: boolean;
  onFrameworkChange: (id: string) => void;
  onSearchChange: (q: string) => void;
  onTypeChange: (t: string) => void;
  onSelectRequirement: (req: RegulatoryRequirement) => void;
}

export const RequirementsCatalogTable: React.FC<RequirementsCatalogTableProps> = ({
  requirements,
  frameworks,
  selectedFrameworkId,
  searchQuery,
  selectedType,
  loading,
  onFrameworkChange,
  onSearchChange,
  onTypeChange,
  onSelectRequirement,
}) => {
  const { sortedItems, sortField, sortDirection, toggleSort } = useTableSort(requirements);

  return (
    <div className="card">
      {/* Toolbar / Filters */}
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          {/* Search Input */}
          <div style={{ minWidth: '220px' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search code/title (e.g. ORO.GEN.200)..."
              style={{ width: '100%', padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
            />
          </div>

          {/* Framework Filter */}
          <div>
            <select
              value={selectedFrameworkId}
              onChange={(e) => onFrameworkChange(e.target.value)}
              style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', backgroundColor: '#fff', color: '#334155' }}
            >
              <option value="">All Regulatory Frameworks</option>
              {frameworks.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.code} - {f.name.substring(0, 36)}...
                </option>
              ))}
            </select>
          </div>

          {/* Requirement Type Filter */}
          <div>
            <select
              value={selectedType}
              onChange={(e) => onTypeChange(e.target.value)}
              style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', backgroundColor: '#fff', color: '#334155' }}
            >
              <option value="">All Rule Types</option>
              <option value="MANDATORY">Mandatory</option>
              <option value="CONDITIONAL">Conditional</option>
              <option value="RECOMMENDED">Recommended</option>
              <option value="GUIDANCE">Guidance</option>
            </select>
          </div>
        </div>

        <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>
          {requirements.length} Requirements Found
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh field="requirementCode" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Rule Code
              </SortableTh>
              <SortableTh field="title" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Title & Scope Summary
              </SortableTh>
              <SortableTh field="frameworkName" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Framework & Document
              </SortableTh>
              <SortableTh field="requirementType" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Type & Legal Level
              </SortableTh>
              <SortableTh field="effectiveDate" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Effective Date
              </SortableTh>
              <SortableTh field="status" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} style={{ textAlign: 'center' }}>
                Status
              </SortableTh>
              <th style={{ textAlign: 'right' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {!loading && sortedItems.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                  No regulatory requirements found matching criteria.
                </td>
              </tr>
            ) : (
              sortedItems.map((req) => (
                <tr
                  key={req.id}
                  onClick={() => onSelectRequirement(req)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <span className="badge-pill badge-info" style={{ fontFamily: 'monospace', fontWeight: 700 }}>
                      {req.requirementCode}
                    </span>
                    {req.isMandatory && (
                      <span className="badge-pill badge-danger" style={{ marginLeft: '6px' }}>
                        MANDATORY
                      </span>
                    )}
                  </td>

                  <td style={{ maxWidth: '300px' }}>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{req.title}</div>
                    <div style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '2px' }}>
                      {req.requirementText}
                    </div>
                  </td>

                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>{req.frameworkCode}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>{req.documentCode}</div>
                  </td>

                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: '#1e293b' }}>{req.requirementType}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{req.normativeLevel}</div>
                  </td>

                  <td style={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '12px', color: '#475569' }}>
                    {req.effectiveDate ? req.effectiveDate.substring(0, 10) : '-'}
                  </td>

                  <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                    <span className={`badge-pill ${req.status === 'ACTIVE' ? 'badge-success' : 'badge-neutral'}`}>
                      {req.status}
                    </span>
                  </td>

                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectRequirement(req);
                      }}
                      className="btn-secondary"
                      style={{ fontSize: '12px', padding: '4px 10px' }}
                    >
                      Details & Compliance →
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
